import type { CSSProperties } from 'react';
import { cn } from '../utils/cn';

export interface KristyLogoProps {
  /** Total height in px. Width auto-scales (preserveAspectRatio). */
  size?: number;
  /**
   * Wordmark style:
   *   - 'full'   "Kristy Software"  (default — landing, panel, footer)
   *   - 'short'  "KSS HRMS"          (only for very tight spaces)
   *   - 'glyph'  icon only           (collapsed sidebar, favicon)
   */
  variant?: 'full' | 'short' | 'glyph';
  /** Override the brand colour (defaults to --color-primary). */
  color?: string;
  className?: string;
  title?: string;
}

/**
 * Kristy Software brand mark — single inline SVG, no third-party deps.
 *
 *   • Glyph: rounded brand-coloured chip with a stacked-K monogram and
 *     a small accent dot. Reads cleanly at favicon size and at 64 px.
 *   • Wordmark: "Kristy" in bold + "SOFTWARE" in light caps. Both
 *     halves use `currentColor` so the parent's CSS `color` (which
 *     resolves to `--color-text-primary`) drives contrast — light
 *     mode picks up #0F172A, dark mode picks up #E2E8F0.
 */
export function KristyLogo({
  size = 32,
  variant = 'full',
  color,
  className,
  title = 'Kristy Software',
}: KristyLogoProps) {
  const showText = variant !== 'glyph';
  const isShort = variant === 'short';
  const brand = color ?? 'var(--color-primary, #3B82F6)';

  const glyphW = 36;
  const wordmarkText = isShort ? 'KSS HRMS' : 'Kristy';
  const subText = isShort ? '' : 'SOFTWARE';
  const textGapPx = 8;
  const wordmarkW = !showText ? 0 : isShort ? 92 : 130;
  const totalW = glyphW + (showText ? textGapPx + wordmarkW : 0);
  const widthPx = Math.round((size * totalW) / 36);

  // currentColor picks up the parent's text colour — set inline so
  // the logo flips with the theme automatically without callers
  // having to wrap it in a `text-content-primary` element.
  const style: CSSProperties = {
    display: 'inline-block',
    verticalAlign: 'middle',
    color: 'var(--color-text-primary, #0F172A)',
  };

  return (
    <svg
      role="img"
      aria-label={title}
      width={widthPx}
      height={size}
      viewBox={`0 0 ${totalW} 36`}
      preserveAspectRatio="xMinYMid meet"
      className={cn('shrink-0', className)}
      style={style}
    >
      <title>{title}</title>

      {/* === GLYPH ============================================== */}
      <rect x="0" y="0" width="36" height="36" rx="8" fill={brand} />
      {/* Stacked K — single path that prints cleanly. */}
      <path
        d="M9 8h4v8.5l7.5-8.5h5l-8.4 9.4 8.7 10.6h-5L13.7 19v9h-4z"
        fill="#FFFFFF"
      />
      {/* Accent dot — keeps the glyph identifiable at favicon size. */}
      <circle cx="29" cy="9" r="2" fill="#FFFFFF" opacity="0.85" />

      {/* === WORDMARK ========================================== */}
      {showText && (
        <g transform={`translate(${glyphW + textGapPx}, 0)`}>
          {isShort ? (
            <text
              x="0"
              y="24"
              fontFamily="Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif"
              fontSize="16"
              fontWeight="800"
              letterSpacing="-0.01em"
              fill="currentColor"
            >
              {wordmarkText}
            </text>
          ) : (
            <>
              <text
                x="0"
                y="22"
                fontFamily="Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif"
                fontSize="17"
                fontWeight="800"
                letterSpacing="-0.01em"
                fill="currentColor"
              >
                {wordmarkText}
              </text>
              <text
                x="0"
                y="33"
                fontFamily="Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif"
                fontSize="9"
                fontWeight="600"
                letterSpacing="0.16em"
                fill="currentColor"
                opacity="0.7"
              >
                {subText}
              </text>
            </>
          )}
        </g>
      )}
    </svg>
  );
}

export default KristyLogo;
